import { Mutations, Resources } from '@erecruit/types';
import { createSlice, PayloadAction, createSelector } from 'redux-starter-kit';
import { selectAppState } from '../../../App/state/selectors';
import {
  PositionRecordMatchCounters,
  PositionRecordSections,
  ModalConfig,
  PositionInfoSections,
} from '../types';
import { PositionField } from '../../AddNewPositionWizard/types';

export interface PositionRecordState {
  isClosingPosition: boolean;
  isLoadingInfo: boolean;
  isLoading: boolean;
  counters?: PositionRecordMatchCounters;
  sections?: PositionRecordSections;
  infoSections?: PositionInfoSections;
  populatedFields?: PositionField[];
  isLoadingModal: boolean;
  error?: string;
  matchStatus: {
    data: Resources.MatchSearch[];
    isLoading: boolean;
    count: number;
  };
}

export type FetchFullPlacementPayload = PayloadAction<{
  positionId: string;
  matchStatus: keyof PositionRecordMatchCounters;
}>;

const initialState: PositionRecordState = {
  isClosingPosition: false,
  isLoading: false,
  isLoadingInfo: false,
  isLoadingModal: false,
  matchStatus: {
    data: [],
    isLoading: false,
    count: 0,
  },
};

const PositionRecordSlice = createSlice({
  slice: 'positionRecord',
  initialState: initialState,
  reducers: {
    approveApprovalPath(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<Mutations.ApproveApprovalPathParams>,
    ) {},
    approveApprovalPathSuccess() {},
    approveApprovalPathFail(state, { payload: error }: PayloadAction<string>) {
      state.error = error;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    closePositionRecord(
      state,
      {
        payload: positionId,
      }: PayloadAction<{ positionId: number; positionName: string }>,
    ) {
      state.isClosingPosition = true;
      state.error = undefined;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    closePositionRecordSuccess(state, { payload }: PayloadAction<unknown>) {
      state.isClosingPosition = false;
    },
    closePositionRecordFail(state, { payload }: PayloadAction<string>) {
      state.isClosingPosition = false;
      state.error = payload;
      state.isLoading = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchPositionRecordInfo(state, { payload: id }: PayloadAction<number>) {
      state.isLoadingInfo = true;
      state.error = undefined;
    },
    fetchPositionRecordInfoSuccess(
      state,
      {
        payload: { infoSections },
      }: PayloadAction<{
        infoSections: PositionInfoSections;
      }>,
    ) {
      state.infoSections = infoSections;
      state.isLoadingInfo = false;
    },
    fetchPositionRecordInfoFail(state, { payload }: PayloadAction<string>) {
      state.isLoadingInfo = false;
      state.error = payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchPositionRecord(state, { payload: id }: PayloadAction<number>) {
      state.isLoading = true;
      state.error = undefined;
    },
    fetchPositionRecordSuccess(
      state,
      {
        payload: { sections, counters },
      }: PayloadAction<{
        sections: PositionRecordSections;
        counters: PositionRecordMatchCounters;
      }>,
    ) {
      state.counters = counters;
      state.sections = sections;
      state.isLoading = false;
    },
    fetchPositionRecordFail(state, { payload }: PayloadAction<string>) {
      state.isLoading = false;
      state.error = payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    savePositionRecord(state, action: PayloadAction<unknown>) {
      state.isLoading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    savePositionRecordSuccess(state, action: PayloadAction<unknown>) {
      state.isLoadingModal = false;
    },
    savePositionRecordFail(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoadingModal = false;
      state.isLoading = false;
    },
    populateEditPositionModal(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<ModalConfig['formSpec']>,
    ) {
      state.isLoadingModal = true;
    },
    populateEditPositionModalSuccess(
      state,
      action: PayloadAction<PositionField[]>,
    ) {
      state.populatedFields = action.payload;
      state.isLoadingModal = false;
    },
    fetchMatchStatus(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: FetchFullPlacementPayload,
    ) {
      state.matchStatus.isLoading = true;
    },
    fetchMatchStatusSuccess(state, action) {
      const { data, count } = action.payload;
      state.matchStatus.data = data;
      state.matchStatus.count = count;
      state.matchStatus.isLoading = false;
    },
    fetchMatchStatusFail(state, action) {
      state.matchStatus.isLoading = false;
      state.error = action.payload;
    },
    rejectApprovalPath(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<Mutations.RejectApprovalPathParams>,
    ) {},
    rejectApprovalPathSuccess() {},
    rejectApprovalPathFail(state, { payload: error }: PayloadAction<string>) {
      state.error = error;
    },
  },
});

const {
  actions,
  reducer,
  selectors: { getPositionRecord, getPopulatedFields },
} = PositionRecordSlice;

export const selectors = {
  selectPositionRecordState: createSelector(
    [selectAppState],
    getPositionRecord,
  ),
  selectPopulatedFields: createSelector([selectAppState], getPopulatedFields),
};

export const {
  approveApprovalPath,
  approveApprovalPathSuccess,
  approveApprovalPathFail,
  closePositionRecord,
  closePositionRecordFail,
  closePositionRecordSuccess,
  fetchPositionRecord,
  fetchPositionRecordFail,
  fetchPositionRecordSuccess,
  fetchPositionRecordInfo,
  fetchPositionRecordInfoFail,
  fetchPositionRecordInfoSuccess,
  populateEditPositionModal,
  populateEditPositionModalSuccess,
  savePositionRecord,
  savePositionRecordFail,
  savePositionRecordSuccess,
  fetchMatchStatus,
  fetchMatchStatusSuccess,
  fetchMatchStatusFail,
  rejectApprovalPath,
  rejectApprovalPathSuccess,
  rejectApprovalPathFail,
} = actions;

export default reducer;
